<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import ListBar from '~/components/global/ListBar.vue'
import useCustomOfferModalStore from '~/stores/modals/useCustomOfferModal'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import trackGtmEvent from '~/utils/tracking/tackGtmEvent'

const props = defineProps<{
  estateData: EstateDetailInterface
}>()

const customOfferModalStore = useCustomOfferModalStore()

const estateType = props.estateData.type

const imageData = getImageSrcSizesData(props.estateData.contact_person.image, {
  includeGenericSrcset: false,
  defaultFormat: '100x200',
  srcsetToGenerate: [
    ['100x200', '1x'],
    ['200x400', '2x'],
    ['400x800', '3x'],
  ],
})

const showPhoneNumber = ref<boolean>(false)

const handleShowPhoneNumber = () => {
  trackGtmEvent('zobrazenie_telefonneho_cisla', {
    estateData: { ...props.estateData },
  })

  showPhoneNumber.value = true
}
</script>

<template>
  <div v-if="estateData.contact_person" class="c-estate-detail-representative">
    <h2 class="c-estate-detail-representative__title">
      {{ $t('pages.estateDetail.representative.title') }}
    </h2>

    <div class="c-estate-detail-representative__representative">
      <img
        v-if="estateData.contact_person.image"
        class="sc-image"
        :src="imageData.src"
        :srcset="imageData.srcset"
        :sizes="imageData.sizes"
        :alt="estateData.contact_person.name"
        loading="lazy"
      />

      <div class="sc-info">
        <strong>{{ estateData.contact_person.name }}</strong>

        <p
          v-if="estateData.contact_person.phone && !showPhoneNumber"
          class="sc-show-phone-number c-link"
          @click="handleShowPhoneNumber"
        >
          {{ $t('pages.estateDetail.representative.showPhoneNumber') }}
        </p>
        <a
          v-else
          class="c-link c-link"
          :href="`tel:${estateData.contact_person.phone}`"
          >{{ estateData.contact_person.phone }}</a
        >
      </div>
    </div>

    <div class="c-estate-detail-representative__list">
      <strong class="sc-title">
        {{ $t('pages.estateDetail.representative.listTitle') }}
      </strong>

      <ListBar>
        <li v-for="i in estateType === 'office' ? 4 : 3" :key="i">
          {{
            $t(`pages.estateDetail.representative.${estateType}.listItem${i}`)
          }}
        </li>
      </ListBar>
    </div>

    <div class="c-estate-detail-representative__action">
      <IButton
        color="secondary"
        :block="true"
        @click="customOfferModalStore.open()"
      >
        {{ $t('pages.estateDetail.representative.action') }}
        <Icon name="arrow-right-thick" />
      </IButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-detail-representative {
  border-left: 4px solid var(--color-secondary);
  background-color: var(--color-green-primary-light-2);
  padding: 40px 50px;
  position: sticky;
  top: calc(var(--header-height) + 20px);

  @include breakpoint-down('xl') {
    padding: 20px 24px;
  }

  @include breakpoint-down('lg') {
    padding: 0;
    border: none;
  }

  &__title {
    font-size: 28px;
    line-height: 0.92;
    padding-bottom: 24px;
    margin-bottom: 30px;
    border-bottom: 1px solid var(--color-border);

    @include breakpoint-down('lg') {
      display: none;
    }
  }

  &__representative {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;

    @include breakpoint-down('lg') {
      display: none;
    }

    .sc-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
    }

    .sc-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 2px;
      font-size: 22px;
      line-height: 1.2;

      .sc-show-phone-number {
        & {
          font-size: 16px;
          margin: 0;
          cursor: pointer;
        }
      }

      .c-link {
        font-size: 16px;
        font-weight: 600;
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    @include breakpoint-down('lg') {
      display: none;
    }

    :deep(.c-list-bar) {
      --c-list-bar--padding: 0 0 0 10px;
    }

    .sc-title {
      margin-bottom: 10px;
    }
  }

  &__action {
    button {
      --button--padding-left: 32px;
      --button--padding-right: 32px;
      --button--font-size: 18px;
      --button--gap: 10px;

      min-height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;

      .ico {
        font-size: 24px;
        top: 1px;
      }
    }
  }
}
</style>
